import { useState, useEffect } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Sparkles, Send, Hotel, Utensils, Ticket, ExternalLink, Tag, Camera, MapPin, Clock, Aperture } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE_URL, API_ENDPOINTS } from '@/lib/apiConfig';

const AFFILIATE_KEYWORDS_AFFILIATES = {
  '酒店': [
    { title: '三亚亚特兰蒂斯酒店', desc: '亲子度假首选·水下套房', price: 3888, commission: '8%' },
    { title: '莫干山郡安里度假酒店', desc: '坐拥山水·避世秘境', price: 1288, commission: '6%' },
    { title: '大理既见·南国社长民宿', desc: '古城核心·纳西文化', price: 488, commission: '10%' },
  ],
  '景点': [
    { title: '北京环球影城', desc: '经典IP主题乐园', price: 638, commission: '3%' },
    { title: '上海迪士尼乐园', desc: '点亮心中奇梦', price: 475, commission: '3%' },
    { title: '长隆野生动物世界', desc: '自驾观赏动物奇观', price: 300, commission: '4%' },
  ],
  '美食': [
    { title: '成都小龙坎火锅', desc: '地道热辣·排队神话', price: 0, commission: '5%' },
    { title: '广州陶陶居早茶', desc: '百年老店·精致茶点', price: 0, commission: '5%' },
    { title: '西安回民街小吃', desc: '千年古都·味觉狂欢', price: 0, commission: '6%' },
  ],
};

// ─── 架构师级机位数据库 ──────────────────────────────────────
const SPOT_RECOMMENDATIONS = {
  '北京': [
    {
      name: '故宫角楼 · 东北角',
      location: '景山公园东门外 · 护城河东岸',
      bestTime: '日出前 30min（蓝调时刻）/ 日落后 20min',
      composition: '水面倒影对称构图 · 低角度 15° · 角楼占画面 1/3',
      proTip: '冬天护城河不结冰 · 倒影最清晰 · 三脚架贴地拍',
      style: '历史厚重感 · 蓝调情绪片',
      crowd: '日出前无人 · 日落前少量摄影老炮',
    },
    {
      name: '景山万春亭',
      location: '景山公园最高点 · 中轴线',
      bestTime: '日落前 1h（黄金时刻）',
      composition: '28mm 广角俯拍紫禁城全貌 · 三分法 · 天空留 1/3',
      proTip: '需提前一天约景山公园门票 · 山顶风大 · 人靠栏杆外侧站',
      style: '皇城全景 · 大气风光',
      crowd: '日落前 30min 占位困难 · 建议 16:30 到位',
    },
    {
      name: '鼓楼东大街红墙',
      location: '鼓楼东门往东 200m · 南侧红墙',
      bestTime: '14:00-16:00（午后斜阳光影）',
      composition: '人像居中 · 树影打在脸上 · 85mm 压缩透视',
      proTip: '墙高约 3m · 侧逆光时红色最饱和 · 配胡同大爷入镜更有生活感',
      style: '京味街拍 · 人文纪实',
      crowd: '工作日几乎无人 · 周末偶有情侣',
    },
    {
      name: '798艺术区 · 涂鸦墙',
      location: '798 小路交叉口 · 包豪斯厂房',
      bestTime: '15:00-17:00（侧光打亮涂鸦）',
      composition: '人物靠墙 · 24mm 广角带环境 · 对角线构图',
      proTip: '涂鸦更换频率高 · 下午光线最均匀 · 周六有市集背景更丰富',
      style: '文艺潮流 · 工业风',
      crowd: '下午人流中等 · 巷子里可避开',
    },
    {
      name: '国贸三期 80F',
      location: '国贸大酒店 · 云·Cipher 酒吧外',
      bestTime: '蓝调时刻（日落后 15-30min）',
      composition: '窗边剪影 · 手持饮品 · 城市灯火做背景',
      proTip: '低消 ¥300/人 · 靠窗位先到先得 · 手机贴玻璃拍可避免反光',
      style: '都市夜景 · 情绪感',
      crowd: '需提前占位 · 周四-六最挤',
    },
    {
      name: '三里屯太古里 · 南区连廊',
      location: '三里屯太古里南区 2F 连廊',
      bestTime: '蓝调时刻 / 全黑后',
      composition: '人像在连廊中 · 商场灯光做背景光斑 · 35mm 平拍',
      proTip: '利用橱窗光线补光 · 周六晚人多更有都市感 · 穿搭亮色更出片',
      style: '时尚都市 · 街拍感',
      crowd: '周末热闹恰好 · 更有氛围',
    },
  ],
  '三亚': [
    {
      name: '椰梦长廊 · 婚纱摄影基地段',
      location: '三亚湾路 · 海坡段（第九小学旁）',
      bestTime: '18:30-19:15（日落+晚霞）',
      composition: '牵手背影 · 占画面下方 1/4 · 天空和椰树留上面',
      proTip: '基地有大量免费道具（白纱/秋千/甲壳虫车）· 日落时海面是金色',
      style: '落日剪影 · 浪漫唯美',
      crowd: '婚纱团队多 · 走远 200m 即可避开',
    },
    {
      name: '蜈支洲岛 · 情人桥',
      location: '蜈支洲岛东区 · 观日岩下方',
      bestTime: '09:00-11:00（顺光海水最蓝）',
      composition: '桥头回望 · 海水做前景 · 超广角 16mm',
      proTip: '首班船上岛 · 前 1h 独享无人桥 · 渐变滤镜压暗天空',
      style: '海岛大片 · 海水蓝调',
      crowd: '11:00 后旅行团抵达 · 务必早去',
    },
    {
      name: '亚特兰蒂斯 · 失落的空间水族馆',
      location: '海棠湾 · 亚特兰蒂斯酒店内',
      bestTime: '14:00-16:00（避开旅行团）',
      composition: '8m 巨型玻璃幕墙前 · 人物剪影对贴玻璃 · 光圈 f/1.4',
      proTip: 'ISO 拉到 1600+ · 关掉闪光灯 · 利用水下蓝光拍情绪片',
      style: '魔幻剪影 · 深蓝孤独感',
      crowd: '下午人少 · 住客免费进 · 游客 ¥198',
    },
    {
      name: '亚龙湾热带天堂 · 索桥',
      location: '过江龙索桥 · 非2取景地',
      bestTime: '08:00-10:00（丁达尔光）',
      composition: '人像站索桥中段 · 两侧雨林做框架 · 中焦 50mm',
      proTip: '清晨水雾营造耶稣光 · 桥会晃 · 快门 1/500 起 · 表情抓拍',
      style: '雨林秘境 · 仙气氛围',
      crowd: '非法定节假日几乎无人',
    },
    {
      name: '海棠湾 · 亚特兰蒂斯无边泳池',
      location: '亚特兰蒂斯酒店 48F 无边际泳池',
      bestTime: '07:00-08:00（晨光顺光）',
      composition: '池边俯拍 · 泳池和大海连成一线 · 手机贴水面',
      proTip: '仅住客可用 · 早起才能无人 · 蓝色纱巾做道具 · 无人机视角更佳',
      style: '网红打卡 · 无限泳池',
      crowd: '清晨 08:00 前基本包场',
    },
  ],
};

// ─── Mock 数据生成器（后续替换为真实后端接口）───
const mockGenerateItinerary = (prompt) => {
  const p = prompt.toLowerCase();
  const isBeijing = p.includes('北京') || p.includes('beijing');
  const isShanghai = p.includes('上海') || p.includes('shanghai');
  const isSanya = p.includes('三亚') || p.includes('sanya');
  const isChengdu = p.includes('成都') || p.includes('chengdu');
  const destination = isBeijing ? '北京' : isShanghai ? '上海' : isSanya ? '三亚' : isChengdu ? '成都' : '目的地';

  const spotRecs = SPOT_RECOMMENDATIONS[destination] || [];
  const spotSection = spotRecs.length > 0 ? `

━━━━━━━━━━━━━━━━━━━━
📸 架构师机位推荐 · ${destination}出片地图
━━━━━━━━━━━━━━━━━━━━
${spotRecs.map((spot, i) => `
━━━ 机位 ${i + 1}: ${spot.name.split(' ·')[0]} ━━━
📍 定位: ${spot.location}
⏰ 最佳时间: ${spot.bestTime}
🔭 构图方案: ${spot.composition}
💡 架构师Tips: ${spot.proTip}
${spot.tag ? `🏷️ 标签: ${spot.tag}` : ''}
`).join('')}` : '';

  if (isBeijing) {
    return `🎯 北京 3日深度游攻略

━━━━━━━━━━━━━━━━━━━━
📅 Day 1｜皇城古韵
━━━━━━━━━━━━━━━━━━━━
  🚩 上午：天安门广场 → 故宫博物院（建议提前预约门票，¥60）
  🚩 中午：王府井步行街觅食 —— 推荐全聚德烤鸭、东来顺涮肉
  🚩 下午：景山公园登顶俯瞰紫禁城 → 南锣鼓巷胡同漫步
  🍜 晚餐：簋街小吃一条街（麻辣小龙虾/爆肚/卤煮）
  🏨 住宿：推荐前门/王府井附近酒店，交通便利
  📸 拍照点: 景山万春亭俯拍 → 角楼蓝调剪影

━━━━━━━━━━━━━━━━━━━━
📅 Day 2｜长城雄风
━━━━━━━━━━━━━━━━━━━━
  🚩 上午：八达岭长城（门票¥40，建议缆车上下）
  🚩 中午：长城脚下农家菜
  🚩 下午：明十三陵 → 鸟巢水立方夜景
  🍜 晚餐：老北京炸酱面、豆汁焦圈体验
  🏨 住宿：国贸商圈高端酒店，地铁直达
  📸 拍照点: 国贸三期 80F 蓝调夜景

━━━━━━━━━━━━━━━━━━━━
📅 Day 3｜学府艺术
━━━━━━━━━━━━━━━━━━━━
  🚩 上午：清华大学/清华大学艺术博物馆
  🚩 中午：五道口韩餐一条街
  🚩 下午：798艺术区看展拍照 → 三里屯太古里逛街
  🍜 晚餐：簋街火锅/护国寺小吃
  📸 拍照点: 798涂鸦墙 + 三里屯连廊

━━━━━━━━━━━━━━━━━━━━
💰 预算参考（人均）
━━━━━━━━━━━━━━━━━━━━
  🏨 住宿：¥400-1200/晚（推荐如家/亚朵/希尔顿）
  🚇 交通：¥100（地铁+公交+门票景区接驳）
  🎫 景点门票：¥200（故宫+长城+十三陵）
  🍜 美食：¥300-500（小吃+正餐+火锅）
  📸 其他：¥100（拍照道具/伴手礼）
  
  💡 合计：约¥1500-2500/人（3天2晚）

${spotSection}`;
  }

  if (isSanya) {
    return `🏝️ 三亚 海岛度假攻略

━━━━━━━━━━━━━━━━━━━━
📅 Day 1｜抵达修整
━━━━━━━━━━━━━━━━━━━━
  🚩 中午：抵达三亚凤凰机场，前往亚龙湾/海棠湾
  🏨 住宿：亚龙湾万豪/丽思卡尔顿，私人沙滩
  🌊 傍晚：三亚湾看日落，椰梦长廊骑行
  🍜 晚餐：第一市场海鲜大餐（龙虾/皮皮虾/螃蟹）
  📸 拍照点: 椰梦长廊日落剪影

━━━━━━━━━━━━━━━━━━━━
📅 Day 2｜海岛探险
━━━━━━━━━━━━━━━━━━━━
  🚩 上午：蜈支洲岛（门票+船票¥130，中国马尔代夫）
  🚩 中午：岛上西餐厅/海鲜BBQ
  🚩 下午：亚龙湾热带天堂森林公园（拍非2取景地）
  🍜 晚餐：海南特色（文昌鸡/加积鸭/东山羊）
  📸 拍照点: 情人桥 + 过江龙索桥

━━━━━━━━━━━━━━━━━━━━
📅 Day 3｜免税购物
━━━━━━━━━━━━━━━━━━━━
  🚩 上午：三亚国际免税城（海棠湾）
  🚩 中午：免税城内美食广场
  🚩 下午：南山文化旅游区（108米海上观音）
  🏨 住宿：海棠湾豪华酒店，泡无边泳池
  🎫 门票：南山¥122 + 蜈支洲¥130
  📸 拍照点: 亚特兰蒂斯水族馆剪影 + 无边泳池

💰 预算参考（人均）：¥3000-6000

${spotSection}`;
  }

  // 通用模板
  return `🎯 ${destination}旅行攻略（3天2晚）

━━━━━━━━━━━━━━━━━━━━
📅 Day 1｜城市初印象
━━━━━━━━━━━━━━━━━━━━
  🚩 上午：抵达${destination}，入住市中心酒店
  🚩 中午：品尝当地特色美食，感受地道风味
  🚩 下午：游览核心景点，拍照打卡地标建筑
  🍜 晚餐：推荐商圈美食广场，各类餐厅任选
  🏨 住宿：建议选择交通便利的酒店或精品民宿

━━━━━━━━━━━━━━━━━━━━
📅 Day 2｜深度探索
━━━━━━━━━━━━━━━━━━━━
  🚩 上午：热门景点游览（需提前预约门票）
  🚩 中午：景区内特色餐饮/农家乐
  🚩 下午：小众文艺街区/古镇老街漫步
  🍜 晚餐：当地特色餐厅，必吃榜推荐

━━━━━━━━━━━━━━━━━━━━
📅 Day 3｜悠闲返程
━━━━━━━━━━━━━━━━━━━━
  🚩 上午：睡到自然醒，退房寄存行李
  🚩 中午：最后一顿美食打卡
  🚩 下午：采购伴手礼 → 机场返程

💰 预算参考（人均）：¥1500-3000
🎫 门票：¥150-300 ｜ 🏨 住宿：¥300-800/晚
🍜 美食：¥200-500 ｜ 🚇 交通：¥50-150`;
};

const TravelAI = () => {
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [visibleAffiliatesState, setVisibleAffiliates] = useState({});

  useEffect(() => {
    if (!result) return;
    const text = result + prompt;
    const newVisibleAffiliates = {};
    if (text.includes('酒店') || text.includes('民宿') || text.includes('住宿')) newVisibleAffiliates['酒店'] = true;
    if (text.includes('景点') || text.includes('门票') || text.includes('乐园')) newVisibleAffiliates['景点'] = true;
    if (text.includes('美食') || text.includes('餐厅') || text.includes('火锅') || text.includes('小吃')) newVisibleAffiliates['美食'] = true;
    setVisibleAffiliates(newVisibleAffiliates);
  }, [result, prompt]);

  const visibleAffiliates = Object.entries(AFFILIATE_KEYWORDS_AFFILIATES).filter(([key]) => visibleAffiliatesState[key]);

  const handleGenerate = () => {
    if (!prompt.trim()) {
      setResult('请输入旅行需求，例：规划北京3日游行程');
      toast.error('请输入旅行需求');
      return;
    }

    setLoading(true);
    setResult('');
    setVisibleAffiliates({});

    // 模拟 1.5s 延迟后 mock 返回
    setTimeout(() => {
      const mockResult = mockGenerateItinerary(prompt.trim());
      setResult(mockResult);
      toast.success('行程生成成功');
      setLoading(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-bg-primary">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-28 pb-20">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-[#E8E4DF] text-sm text-primary mb-4">
            <Sparkles className="w-4 h-4" />
            AI 智能旅行规划
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-3 tracking-tight">
            输入需求，秒级生成攻略
          </h1>
          <p className="text-text-secondary max-w-xl mx-auto">
            描述你的旅行计划，AI 为你定制完整行程 + 架构师级机位推荐
          </p>
        </div>

        <Card className="border-0 shadow-card rounded-card bg-white mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                id="inputBox"
                placeholder="请输入旅行需求，例：规划北京3日游行程"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleGenerate(); } }}
                className="flex-1 rounded-smwidget border-[#E8E4DF] bg-bg-secondary text-text-primary placeholder:text-text-tertiary focus:border-primary focus:ring-primary/20 h-12"
              />
              <Button
                id="generateBtn"
                type="button"
                onClick={handleGenerate}
                disabled={loading}
                className="bg-primary hover:bg-primary-hover text-white rounded-smwidget px-6 h-12 shadow-btn hover:shadow-btn-hover transition-all whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    生成中...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    生成旅行攻略
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-card rounded-card bg-white">
          <CardContent className="p-6">
            <div
              id="resultBox"
              className="w-full min-h-[400px] bg-white text-text-primary text-sm leading-relaxed whitespace-pre-wrap font-sans break-words"
            >
              {result ? (
                <div className="prose prose-slate max-w-none">
                  {result}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-text-tertiary py-20">
                  <Sparkles className="w-10 h-10 mb-3 opacity-40" />
                  <p>行程攻略和拍照机位推荐将在这里展示...</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 美团联盟导流卡片 - 根据攻略内容自动匹配 */}
        {result && visibleAffiliates.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-medium text-text-primary">相关推荐</h3>
              <span className="text-[10px] px-1.5 py-0.5 rounded text-text-tertiary" style={{ background: 'rgba(106,179,214,0.06)' }}>联盟</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {visibleAffiliates.map(([key, items]) => {
                const icon = key === '酒店' ? Hotel : key === '景点' ? Ticket : Utensils;
                const Icon = icon;
                return (items || []).map((item, i) => (
                  <a key={`${key}-${i}`} href="#" target="_blank" rel="noopener noreferrer"
                    className="block p-3 rounded-xl text-white hover:shadow-lg transition-all"
                    style={{ background: 'linear-gradient(135deg, rgba(106,179,214,0.15), rgba(5,85,135,0.1))', border: '1px solid rgba(106,179,214,0.15)' }}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <Icon className="w-5 h-5 shrink-0" style={{ color: '#7eccff' }} />
                      <span className="text-[10px] cd flex items-center gap-0.5">
                        <ExternalLink className="w-2.5 h-2.5" />美团推荐
                      </span>
                    </div>
                    <div className="text-sm font-medium cb mb-1">{item.title}</div>
                    <div className="text-xs cd mb-2">{item.desc}</div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: '#ffd857' }}>佣金{item.commission}</span>
                      <span className="text-sm font-medium" style={{ color: item.price === 0 ? '#34d399' : '#7eccff' }}>
                        {item.price === 0 ? '预订领券' : `¥${item.price}起`}
                      </span>
                    </div>
                  </a>
                ));
              })}
            </div>
            <p className="text-[10px] cd mt-2 text-center">以上推荐含联盟推广链接，预订成功创作者获得佣金支持创作</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TravelAI;
